import { useStorefront, type Currency } from '@/hooks/useStorefront';
import { useExchangeRate } from '@/hooks/useExchangeRate';
import { formatPrice, formatPriceForCurrency } from '@/lib/formatPrice';
import { convertPrice } from '@/lib/priceConversion';
import { roundPrice } from '@/lib/priceRounding';
import { useVariationOverrides } from '@/hooks/useVariationOverrides';

export interface DisplayPrice {
  amount: number;
  currency: Currency;
  formatted: string;
}

export interface UseDisplayPriceResult {
  price: DisplayPrice | null;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Displays a CHF-basis price in the active storefront's currency.
 *
 * CH fastpath: returns { amount: chf, currency: 'CHF', formatted: formatPrice(chf) }
 * — byte-identical to the pre-P2.2 output.
 *
 * useExchangeRate is always called (Rules of Hooks); its network fetch is
 * gated to non-CH storefronts, so CH performs no request.
 */
export function useDisplayPrice(
  chf: number | null | undefined,
  variationId?: string | null,
): UseDisplayPriceResult {
  const { storefront, currency } = useStorefront();
  const { getRate, isLoading, isError } = useExchangeRate();
  const { get: getOverride } = useVariationOverrides();

  // CH fastpath — ignore rate result entirely.
  if (storefront === 'ch') {
    if (chf == null) return { price: null, isLoading: false, isError: false };
    return {
      price: { amount: chf, currency: 'CHF', formatted: formatPrice(chf) },
      isLoading: false,
      isError: false,
    };
  }

  if (chf == null) return { price: null, isLoading: false, isError: false };

  // Precedence 2: manual per-country override — take 1:1, no FX, no rounding.
  const ov = getOverride(variationId);
  if (ov) {
    return {
      price: { amount: ov.price, currency: ov.currency, formatted: formatPriceForCurrency(ov.price, ov.currency) },
      isLoading: false,
      isError: false,
    };
  }

  // Precedence 3: auto conversion CHF -> currency -> rounding.
  const rate = getRate(currency);
  if (isLoading && rate == null) return { price: null, isLoading: true, isError: false };
  if (isError || rate == null) return { price: null, isLoading: false, isError: true };

  const converted = convertPrice(chf, rate);
  const rounded = roundPrice(converted, currency);
  return {
    price: { amount: rounded, currency, formatted: formatPriceForCurrency(rounded, currency) },
    isLoading: false,
    isError: false,
  };
}

export interface UseDisplayPriceSumResult {
  sum: DisplayPrice | null;
  lines: number[]; // per-line displayed amounts in target currency (CH: unchanged CHF values)
  isLoading: boolean;
  isError: boolean;
}

export interface DisplayLineInput {
  chf: number;
  variationId?: string | null;
}

function isObjectItem(v: number | DisplayLineInput): v is DisplayLineInput {
  return typeof v === 'object' && v !== null;
}

/**
 * Sums pre-rounded line amounts so the visible total always equals the sum
 * of the visible line prices — respecting per-line overrides (P2.3). P2.4
 * will use this same rule as the billed total. CH: sum of CHF values,
 * byte-identical to today's subtotal display.
 *
 * Accepts either a bare CHF number[] (legacy) or {chf, variationId}[] so
 * overrides can participate in the total.
 */
export function useDisplayPriceSum(
  input: Array<number | DisplayLineInput>,
): UseDisplayPriceSumResult {
  const { storefront, currency } = useStorefront();
  const { getRate, isLoading, isError } = useExchangeRate();
  const { get: getOverride } = useVariationOverrides();

  const items: DisplayLineInput[] = (input ?? []).map((v) =>
    isObjectItem(v) ? { chf: Number(v.chf) || 0, variationId: v.variationId ?? null } : { chf: Number(v) || 0, variationId: null },
  );

  if (storefront === 'ch') {
    const total = items.reduce((a, it) => a + it.chf, 0);
    return {
      sum: { amount: total, currency: 'CHF', formatted: formatPrice(total) },
      lines: items.map((it) => it.chf),
      isLoading: false,
      isError: false,
    };
  }

  const rate = getRate(currency);
  // If any line lacks an override we need the rate. If every line has an override,
  // we can produce a total without waiting for FX.
  const needsRate = items.some((it) => !getOverride(it.variationId ?? undefined));
  if (needsRate) {
    if (isLoading && rate == null) return { sum: null, lines: [], isLoading: true, isError: false };
    if (isError || rate == null) return { sum: null, lines: [], isLoading: false, isError: true };
  }

  const lineParts = items.map((it) => {
    const ov = getOverride(it.variationId ?? undefined);
    if (ov) return { amount: ov.price, currency: ov.currency };
    return { amount: roundPrice(convertPrice(it.chf, rate!), currency), currency };
  });
  // If overrides use a different currency than the storefront currency, the sum
  // is not well-defined. In practice the DB trigger forces override.currency ===
  // storefront currency, so this is always consistent.
  const total = lineParts.reduce((a, l) => a + l.amount, 0);
  return {
    sum: { amount: total, currency, formatted: formatPriceForCurrency(total, currency) },
    lines: lineParts.map((l) => l.amount),
    isLoading: false,
    isError: false,
  };
}